// 8.3.6 — Practice modules + shared pop-ups (merged into the locale as `pm`).
const pm = {
  common: {
    proFeature: "{name} is a Pro feature.", micUnavailable: "Microphone not available.",
    noAudio: "No audio captured — try again.", voiceUnavailable: "Voice unavailable right now.",
    messageFailed: "Message failed. Try again.",
  },
  scores: { accuracy: "Accuracy", fluency: "Fluency", completeness: "Completeness", grammar: "Grammar", persuasiveness: "Persuasiveness" },
  roommate: {
    subtitle: "Chat with an AI partner. They'll correct you gently and teach you new words.",
    hint: "Pro tip: reply in full sentences — your Roommate corrects you gently and saves new words for you.",
    sayHello: "Say hello to start!", typeIn: "Type in {lang}…", typeTarget: "Type in your target language…",
    wordsToday: "Words taught today", newWordsHere: "New words appear here.",
    corrections: "Gentle corrections", doingGreat: "You're doing great!",
    personas: {
      roommate: { label: "Roommate", scenario: "chatting at home in the kitchen" },
      cafe: { label: "Café", scenario: "ordering coffee at a busy café" },
      market: { label: "Market", scenario: "buying fruit at a street market" },
      clinic: { label: "Clinic", scenario: "a check-up at the clinic" },
    },
  },
  accent: {
    subtitle: "Say the phrase out loud. We'll score your pronunciation, then drill the weakest part. Your audio is never stored.",
    hint: "Pro tip: use headphones and record in a quiet spot for the most accurate score. Tap the phrase's speaker to hear it first.",
    unavailable: "AccentCoach isn't available for this language yet.",
    sayThis: "Say this · {level}", hear: "Hear it", playing: "Playing…", newPhrases: "New phrases",
    scoring: "Scoring…", recording: "Recording… tap to stop", tapRecord: "Tap to record",
    weHeard: "We heard:", focusOn: "Focus on:", tryAgain: "Try again", scoreFailed: "Could not score that. Try again.",
  },
  debate: {
    showMeaning: "Show meaning", hideMeaning: "Hide meaning", topicFailed: "Couldn't create a debate topic in {lang}. Try again.",
    subtitle: "Argue your assigned side. The AI pushes back and scores your grammar, fluency and persuasiveness. Best for B2+.",
    hintPre: "Pro tip: you can argue by", hintVoice: "voice", hintPost: "too — tap the mic to speak your point and get it transcribed and scored.",
    startTitle: "Start a debate", topicPlaceholder: "Optional: enter a topic, or leave blank for a surprise",
    settingUp: "Setting up…", begin: "Begin debate", topic: "Topic", mustArgue: "You must argue:",
    sides: { for: "For", against: "Against" }, you: "You", opponent: "Opponent", roundScores: "Round scores",
    improve: "Improve:", thinking: "Opponent is thinking…", argPlaceholder: "Make your argument…",
    voiceTitle: "Argue by voice", transcribing: "🎙️ Transcribing your argument…",
    startFailed: "Could not start debate.", roundFailed: "Round failed.", noAudio: "No audio captured.",
  },
  quiz: {
    subtitle: "Spaced-repetition quizzes built from your own vocab bank.",
    hint: "Pro tip: \"Review due\" surfaces the words about to fade from memory — do these first to lock them in for good.",
    wordsDue: { one: "word due — review now", other: "words due — review now" },
    topicTitle: "Quiz a topic", topicPlaceholder: "e.g. food, travel", generate: "Generate",
    building: "Building your quiz…", question: "Question {n}/{total}",
    types: { mcq: "Multiple choice", fill: "Fill in the blank" },
    answerPlaceholder: "Type your answer", check: "Check", correct: "{correct}/{total} correct",
    xpEarned: { one: "+{count} XP earned", other: "+{count} XP earned" }, back: "Back to QuizForge",
    noQuestions: "No questions generated.", buildFailed: "Could not build quiz.", gradeFailed: "Grading failed.",
  },
  story: {
    title: "Story Mode", subtitle: "A story written just for you — starring the words you've captured.",
    hint: "Pro tip: capture more words in LanguageLoop, then tap the speaker on any chapter to hear it read aloud in your chosen voice.",
    beginTitle: "Begin a new story", beginSub: "Pick a genre — we'll weave in your vocab, chapter by chapter.",
    themes: { mystery: "Mystery", adventure: "Adventure", romance: "Romance", scifi: "Sci-Fi", slice: "Slice of life" },
    ownIdea: "…or type your own idea", writing: "Writing…", start: "Start",
    chapters: { one: "{count} chapter", other: "{count} chapters" }, chapter: "Chapter {n}",
    listen: "Listen", translation: "Translation", writingNext: "Writing the next chapter…",
    continue: "Continue the story", different: "Start a different story",
    unlocked: "Chapter {n} unlocked! +10 XP", startFailed: "Could not start your story.", continueFailed: "Could not continue the story.",
  },
  mission: {
    steps: { warm_up: "Warm-up", core_task: "Your mission", extension: "Extra challenge" },
    keyWords: "Key words & phrases", challenge: "Conversation challenge", fromBrief: "From your mission brief.",
    newWordsHint: "New words you meet appear here — tap Add to save them.", noCorrections: "Great — no corrections so far!",
    markFailed: "Could not mark complete.", messageFailed: "Message failed.",
    added: { one: "Added {count} to your Vocab Bank", other: "Added {count} to your Vocab Bank" },
    addFailed: "Couldn't add to Vocab Bank.", listen: "Listen",
  },
  library: {
    title: "Mission Library", newMission: "New Mission",
    intro: "Your private, reusable fluency missions. Assigning one never changes the original — and reusing a saved mission costs no AI generation.",
    search: "Search by topic or title…", allLanguages: "All languages", allLevels: "All levels",
    sortRecent: "Recently edited", sortLastUsed: "Last used", empty: "Your library is empty",
    emptySub: "Create a mission here, or save drafts from any class's “Create Mission”.",
    used: "Used", lastUsed: "Last used {date}", assign: "Assign", edit: "Edit", duplicate: "Duplicate", delete: "Delete",
    duplicated: "Duplicated", dupFailed: "Could not duplicate",
    confirmDelete: "Delete this mission from your library? Assignments already created are unaffected.",
    deleted: "Deleted", delFailed: "Could not delete", assignTitle: "Assign “{title}”",
    chooseClasses: "Choose classes (you teach)", noClasses: "You have no active classes.",
    dueOptional: "Due date (optional)", assignMission: "Assign mission", pickClass: "Pick at least one class.",
    assigned: { one: "Assigned to {count} class.", other: "Assigned to {count} classes." }, assignFailed: "Could not assign.",
    close: "Close",
  },
  studio: {
    needPractice: "Tell us what your students should practise.", genFailed: "Generation failed. Try again.",
    sectionRegen: "Section regenerated", missionRegen: "Mission regenerated", regenFailed: "Regeneration failed.",
    assigned: "Mission assigned to your class!", updated: "Mission updated.", saved: "Saved to your Mission Library.",
    saveFailed: "Could not save.", assignFailed: "Could not assign.",
    editTitle: "Edit Mission", createTitle: "Create a Fluency Mission",
    classIntro: "Just tell us the goal — GetFluentLoop already knows the language and level.",
    language: "Language", level: "Level", practiceQ: "What should your students practise?",
    practicePlaceholder: "e.g. Ordering food at a restaurant and making polite requests",
    designing: "Designing mission…", generateDraft: "Generate mission draft", regenAll: "Regenerate all",
    minutes: "~{count} min", regenerate: "regenerate",
    f: {
      title: "Mission title", intro: "Student intro", objective: "Learning objective", scenario: "Scenario",
      warmup: "Warm-up", core: "Core task", challenge: "Conversation challenge",
      mode: "Conversation mode (how students complete the challenge)",
      modeAny: "Any mode — student picks Speak or Type", modeVoice: "Voice encouraged — nudge speaking (Type always available)",
      extension: "Extra challenge (optional)", grammar: "Grammar / focus", minutes: "Est. minutes", culture: "Cultural note",
      vocab: "Target vocabulary (one per line: word | translation)", phrases: "Useful phrases (one per line: phrase | translation)",
      criteria: "Success criteria (one per line)", due: "Due date (optional — a gentle nudge, never blocks students)",
    },
    saveChanges: "Save changes", saveLibrary: "Save to Library", assignClass: "Assign to class",
  },
  paywall: {
    benefits: ["Live conversations with Fluent Friend", "FluentLens", "Voice Journal", "Intelligent Today", "Vocabulary & spaced repetition", "Personal Language Brain", "All supported languages"],
    tagline: "Your life becomes your language course.", month: "month",
    starting: "Starting…", subscribe: "Subscribe to Pro", later: "Maybe later", checkoutFailed: "Could not start checkout. Please try again.",
  },
  consent: {
    titleCamera: "Allow camera access?", titleMic: "Allow microphone access?",
    needsCamera: "{brand} needs your camera to recognise objects.", needsMic: "{brand} needs your microphone to score your speech.",
    rawCamera: "Your raw photo is processed in memory and {del}. We only keep the word.",
    rawMic: "Your raw recording is processed in memory and {del}. We only keep the score.",
    deletedCamera: "deleted immediately", deletedMic: "deleted immediately", grant: "I consent — continue", notNow: "Not now", close: "Close",
  },
};
export default pm;
